/**
 * Desktop settings — which style the Desktop uses. Opened from the Windows
 * taskbar's gear, the desktop's context menu ("Desktop style…"), and the Mac
 * menu bar ("Fias ▸ Settings…").
 */
import { Monitor, Laptop } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { DesktopStyle } from '../../lib/folder-view';

const STYLES: Array<{ style: DesktopStyle; title: string; blurb: string; icon: typeof Monitor }> = [
  {
    style: 'windows',
    title: 'Windows',
    blurb:
      'A taskbar along the bottom and the Trash on the desktop. Windows have minimise, maximise and close on the right, with Back, Up, Sort and View in the command bar. Enter opens, F2 renames, Delete moves to the Trash.',
    icon: Monitor,
  },
  {
    style: 'mac',
    title: 'Mac',
    blurb:
      'A menu bar across the top and a Dock with the Trash. Finder-style windows with the traffic lights, a sidebar, and Icons, List, Columns and Gallery views. ⌘O opens, Enter renames, ⌘⌫ moves to the Trash, Space previews.',
    icon: Laptop,
  },
];

export function DesktopSettingsDialog({
  open,
  style,
  onStyle,
  onClose,
}: {
  open: boolean;
  style: DesktopStyle;
  onStyle: (style: DesktopStyle) => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Desktop style</DialogTitle>
          <DialogDescription>
            How your Desktop looks and behaves. Your files, folders and arrangement stay the same —
            this browser remembers your choice.
          </DialogDescription>
        </DialogHeader>
        <div role="radiogroup" aria-label="Desktop style" className="grid gap-3">
          {STYLES.map(({ style: option, title, blurb, icon: Icon }) => {
            const on = style === option;
            return (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => onStyle(option)}
                className={cn(
                  'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                  on
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'border-border hover:bg-muted/50',
                )}
              >
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                <span>
                  <span className="block text-sm font-medium text-foreground">{title}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{blurb}</span>
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
